/**
 * TeamMessageContext - Global notification system for team messages
 * Polls for new messages and plays beep across ALL pages (not just sign-in)
 */
import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "./AuthContext";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { playDoubleBeep } from "@/utils/notificationSound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamMessageContext = createContext(null);

// Play notification beep - uses TRUE double beep from notificationSound.js
const playNotificationBeep = () => {
  console.log("[TeamMessage] Playing TRUE double beep notification");
  try {
    playDoubleBeep();
    return true;
  } catch (error) {
    console.warn("Could not play notification sound:", error);
    return false;
  }
};

export const TeamMessageProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [hasNewMessage, setHasNewMessage] = useState(false);
  
  // Determine if authenticated based on token presence
  const isAuthenticated = !!token;
  
  // Use refs to track state without causing re-renders or callback recreation
  const lastMessageCountRef = useRef(0);
  const lastUpdatedByRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const pollIntervalRef = useRef(null);
  const userEmailRef = useRef(null);

  // Keep user email ref in sync
  useEffect(() => {
    userEmailRef.current = user?.email;
  }, [user?.email]);

  // Get today's date
  const today = format(new Date(), 'yyyy-MM-dd');

  // Poll for new messages - use refs to avoid recreating this callback
  const checkForNewMessages = useCallback(async () => {
    if (!isAuthenticated || !userEmailRef.current) return;
    
    try {
      const response = await axios.get(`${API}/team-messages/today?date=${today}`);
      const data = response.data;
      
      if (data.message) {
        // Count messages by splitting on newlines
        const messages = data.message.split('\n').filter(line => line.trim());
        const messageCount = messages.length;
        const updatedBy = data.updatedBy;
        
        // Check if there's a new message from someone else
        if (!isInitialLoadRef.current && 
            messageCount > lastMessageCountRef.current &&
            updatedBy && 
            updatedBy.toLowerCase() !== userEmailRef.current?.toLowerCase()) {
          
          console.log(`[TeamMessage] New message detected! From: ${data.updatedByName}, Count: ${messageCount} (was ${lastMessageCountRef.current})`);
          
          // Play beep
          const beeped = playNotificationBeep();
          console.log(`[TeamMessage] Beep played: ${beeped}`);
          
          // Show toast notification
          toast.info(
            `📢 New message from ${data.updatedByName || 'team member'}`,
            { 
              duration: 5000,
              action: {
                label: 'View',
                onClick: () => window.location.href = `/signin/${today}`
              }
            }
          );
          
          setHasNewMessage(true);
          
          // Auto-clear the new message indicator after 10 seconds
          setTimeout(() => setHasNewMessage(false), 10000);
        }
        
        // Update refs (doesn't cause re-render)
        lastMessageCountRef.current = messageCount;
        lastUpdatedByRef.current = updatedBy;
      }
      
      isInitialLoadRef.current = false;
    } catch (error) {
      // Silently fail - don't spam console on network errors
      if (error.response?.status !== 401) {
        console.debug("[TeamMessage] Poll error:", error.message);
      }
    }
  }, [isAuthenticated, today]); // Removed lastMessageCount from dependencies!

  // Start polling when authenticated
  useEffect(() => {
    console.log("[TeamMessage] Auth state:", { isAuthenticated, hasUser: !!user, email: user?.email });
    
    if (!isAuthenticated || !user) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }
    
    console.log("[TeamMessage] Starting poll for user:", user.email);
    
    // Initial check
    checkForNewMessages();
    
    // Poll every 5 seconds
    pollIntervalRef.current = setInterval(checkForNewMessages, 5000);
    
    return () => {
      if (pollIntervalRef.current) {
        console.log("[TeamMessage] Stopping poll");
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, user, checkForNewMessages]);

  // Reset on date change
  useEffect(() => {
    console.log("[TeamMessage] Date changed, resetting state");
    isInitialLoadRef.current = true;
    lastMessageCountRef.current = 0;
    lastUpdatedByRef.current = null;
    setHasNewMessage(false);
  }, [today]);

  // Method to notify that the current user just sent a message
  // This prevents the next poll from triggering a false beep
  const notifyMessageSent = useCallback((newCount) => {
    lastMessageCountRef.current = newCount;
    lastUpdatedByRef.current = userEmailRef.current;
  }, []);

  // Method to manually trigger beep (for testing or other uses)
  const triggerBeep = useCallback(() => {
    return playNotificationBeep();
  }, []);

  // Method to mark messages as seen
  const clearNewMessageFlag = useCallback(() => {
    setHasNewMessage(false);
  }, []);

  return (
    <TeamMessageContext.Provider value={{ 
      hasNewMessage, 
      triggerBeep, 
      clearNewMessageFlag,
      notifyMessageSent,
      lastMessageCount: lastMessageCountRef.current 
    }}>
      {children}
    </TeamMessageContext.Provider>
  );
};

export const useTeamMessages = () => {
  const context = useContext(TeamMessageContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return { 
      hasNewMessage: false, 
      triggerBeep: () => false, 
      clearNewMessageFlag: () => {},
      notifyMessageSent: () => {},
      lastMessageCount: 0 
    };
  }
  return context;
};

export default TeamMessageContext;
